import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../assets/styles/auth/login.scss';
import logo from '../assets/img/logo.png';
import axios from './authInterceptor';

const FirstPasswordChange = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [isLoading, setIsLoading] = useState(false);
    const [changeError, setChangeError] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    const validate = () => {
        let valid = true;
        const newErrors = { currentPassword: '', newPassword: '', confirmPassword: '' };

        if (!formData.currentPassword) {
            newErrors.currentPassword = 'La contraseña actual es obligatoria';
            valid = false;
        }

        if (!formData.newPassword) {
            newErrors.newPassword = 'La nueva contraseña es obligatoria';
            valid = false;
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Debe tener al menos 8 caracteres';
            valid = false;
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
            newErrors.newPassword = 'Debe contener al menos: 1 mayúscula, 1 minúscula y 1 número';
            valid = false;
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirma tu nueva contraseña';
            valid = false;
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden';
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validate()) {
            setIsLoading(true);
            setChangeError('');
            setIsSuccess(false);

            try {
                const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
                
                console.log('🔐 [FRONTEND] Iniciando cambio de contraseña para usuario:', usuario.id);
                
                const response = await axios.post('/update-password', {
                    asesorId: usuario.id,
                    currentPassword: formData.currentPassword,
                    newPassword: formData.newPassword
                }, {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    timeout: 10000
                });

                console.log('🔐 [FRONTEND] Respuesta del servidor:', response.data);

                if (response.data.Status === "Success") {
                    console.log('🔐 [FRONTEND] ✅ Contraseña actualizada exitosamente');
                    
                    // Marcar como exitoso
                    setIsSuccess(true);
                    
                    // Limpiar el flag de cambio de contraseña requerido
                    localStorage.removeItem('requiresPasswordChange');
                    
                    // Actualizar datos del usuario en localStorage para reflejar que ya no requiere cambio
                    const usuarioActualizado = {
                        ...usuario,
                        password_cambiado: true
                    };
                    localStorage.setItem('usuario', JSON.stringify(usuarioActualizado));
                    
                    console.log('🔐 [FRONTEND] Usuario actualizado en localStorage');
                    
                    // Esperar 2 segundos para mostrar el mensaje de éxito y luego redireccionar
                    setTimeout(() => {
                        console.log('🔐 [FRONTEND] Redirigiendo al dashboard...');
                        navigate('/dashboard/resumen');
                    }, 2000);
                    
                } else {
                    console.log('🔐 [FRONTEND] ❌ Error en la respuesta:', response.data.Error);
                    setChangeError(response.data.Error || 'Error al actualizar contraseña');
                }
            } catch (error) {
                console.error('🔐 [FRONTEND] ❌ Error actualizando contraseña:', error);
                
                if (error.response) {
                    const errorMessage = error.response.data?.Error || 'Error del servidor';
                    console.log('🔐 [FRONTEND] Error del servidor:', errorMessage);
                    setChangeError(errorMessage);
                } else if (error.request) {
                    console.log('🔐 [FRONTEND] Error de conexión');
                    setChangeError('Error de conexión. Verifica que el servidor esté ejecutándose.');
                } else {
                    console.log('🔐 [FRONTEND] Error inesperado:', error.message);
                    setChangeError('Error inesperado. Por favor, inténtalo de nuevo.');
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.id]: e.target.value,
        });
        setErrors({
            ...errors,
            [e.target.id]: '',
        });
        if (changeError) {
            setChangeError('');
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords({
            ...showPasswords,
            [field]: !showPasswords[field]
        });
    };

    return (
        <div className="loginContainer">
            <div className="loginBox">
                <img src={logo} alt="Logo" className="logo" />
                <h1 className="title">Cambio de Contraseña Obligatorio</h1>
                <p className="subtitle">
                    Por seguridad, debes cambiar tu contraseña antes de continuar
                </p>
                
                {isSuccess ? (
                    <div className="successMessage">
                        <div className="successIcon">✅</div>
                        <h3>¡Contraseña actualizada exitosamente!</h3>
                        <p>Redirigiendo al dashboard...</p>
                        <div className="loadingSpinner"></div>
                    </div>
                ) : (
                    <form className="form" onSubmit={handleSubmit}>
                        <div className="inputGroup passwordGroup">
                            <label htmlFor="currentPassword">Contraseña Actual</label>
                            <div className="passwordInputWrapper">
                                <input
                                    type={showPasswords.current ? 'text' : 'password'}
                                    id="currentPassword"
                                    placeholder="Ingresa tu contraseña actual"
                                    value={formData.currentPassword}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    className="togglePasswordBtn"
                                    onClick={() => togglePasswordVisibility('current')}
                                    disabled={isLoading}
                                >
                                    <i className={`ri-${showPasswords.current ? 'eye-line' : 'eye-off-line'}`}></i>
                                </button>
                            </div>
                            {errors.currentPassword && <p className="errorText">{errors.currentPassword}</p>}
                        </div>

                        <div className="inputGroup passwordGroup">
                            <label htmlFor="newPassword">Nueva Contraseña</label>
                            <div className="passwordInputWrapper">
                                <input
                                    type={showPasswords.new ? 'text' : 'password'}
                                    id="newPassword"
                                    placeholder="Ingresa tu nueva contraseña"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    className="togglePasswordBtn"
                                    onClick={() => togglePasswordVisibility('new')}
                                    disabled={isLoading}
                                >
                                    <i className={`ri-${showPasswords.new ? 'eye-line' : 'eye-off-line'}`}></i>
                                </button>
                            </div>
                            {errors.newPassword && <p className="errorText">{errors.newPassword}</p>}
                        </div>

                        <div className="inputGroup passwordGroup">
                            <label htmlFor="confirmPassword">Confirmar Nueva Contraseña</label>
                            <div className="passwordInputWrapper">
                                <input
                                    type={showPasswords.confirm ? 'text' : 'password'}
                                    id="confirmPassword"
                                    placeholder="Confirma tu nueva contraseña"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    className="togglePasswordBtn"
                                    onClick={() => togglePasswordVisibility('confirm')}
                                    disabled={isLoading}
                                >
                                    <i className={`ri-${showPasswords.confirm ? 'eye-line' : 'eye-off-line'}`}></i>
                                </button>
                            </div>
                            {errors.confirmPassword && <p className="errorText">{errors.confirmPassword}</p>}
                        </div>

            

                        <hr className="divider" />
                        
                        {changeError && <p className="errorText">{changeError}</p>}
                        
                        <button type="submit" className="submitButton" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <span>Actualizando contraseña...</span>
                                    <div className="buttonSpinner"></div>
                                </>
                            ) : (
                                'Cambiar Contraseña'
                            )}
                        </button>
                    </form>
                )}
                
                <div className="registerLink">
                    <p>¿Tienes problemas? <Link to="/dashboard/resumen">Contacta al administrador</Link></p>
                </div>
            </div>
            
            <style jsx>{`
                .password-requirements {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 6px;
                    padding: 12px;
                    margin: 16px 0;
                }
                .password-requirements h4 {
                    margin: 0 0 8px 0;
                    font-size: 14px;
                    color: #495057;
                }
                .password-requirements ul {
                    margin: 0;
                    padding-left: 16px;
                    font-size: 12px;
                    color: #6c757d;
                }
                .password-requirements li {
                    margin: 4px 0;
                }
                .successMessage {
                    text-align: center;
                    padding: 40px 20px;
                }
                .successIcon {
                    font-size: 48px;
                    margin-bottom: 16px;
                }
                .successMessage h3 {
                    color: #28a745;
                    margin-bottom: 8px;
                }
                .successMessage p {
                    color: #6c757d;
                    margin-bottom: 20px;
                }
                .loadingSpinner {
                    width: 24px;
                    height: 24px;
                    border: 3px solid #f3f3f3;
                    border-top: 3px solid #007bff;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto;
                }
                .buttonSpinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid #ffffff;
                    border-top: 2px solid transparent;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-left: 8px;
                    display: inline-block;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .submitButton {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            `}</style>
        </div>
    );
};

export default FirstPasswordChange;
